import React, { useState } from 'react';
import { 
  Box, Container, Typography, TextField, Button, 
  Paper, IconButton, Select, MenuItem, FormControl, InputLabel, 
 Grid, Alert
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import surveyService from '../services/surveyService';

const QUESTION_TYPES = [
  { value: 'Text', label: 'Short Answer' },
  { value: 'Paragraph', label: 'Long Answer' },
  { value: 'MultipleChoice', label: 'Multiple Choice (Pick One)' },
  { value: 'Checkbox', label: 'Checkboxes' },
  { value: 'YesNo', label: 'Yes / No' },
  { value: 'ImageUpload', label: 'Image / Photo Upload' }
];

const CreateSurveyPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Core Survey Data
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [questions, setQuestions] = useState([
    { id: 1, questionText: '', type: 'Text', options: ['Option 1'] }
  ]);

  // --- LOGIC: QUESTION MANAGEMENT ---

  const addQuestion = () => {
    const newId = questions.length + 1;
    setQuestions([
      ...questions, 
      { id: newId, questionText: '', type: 'Text', options: ['Option 1'] }
    ]);
  };

  const removeQuestion = (indexToRemove) => {
    setQuestions(questions.filter((_, index) => index !== indexToRemove));
  };

  const updateQuestion = (index, field, value) => {
    const updatedQuestions = [...questions];
    updatedQuestions[index][field] = value;
    setQuestions(updatedQuestions);
  };

  // --- LOGIC: OPTIONS MANAGEMENT (For Multiple Choice) ---

  const addOption = (questionIndex) => {
    const updatedQuestions = [...questions];
    updatedQuestions[questionIndex].options.push(`Option ${updatedQuestions[questionIndex].options.length + 1}`);
    setQuestions(updatedQuestions);
  };

  const updateOption = (questionIndex, optionIndex, value) => {
    const updatedQuestions = [...questions];
    updatedQuestions[questionIndex].options[optionIndex] = value;
    setQuestions(updatedQuestions);
  };

  const removeOption = (questionIndex, optionIndex) => {
    const updatedQuestions = [...questions];
    updatedQuestions[questionIndex].options = updatedQuestions[questionIndex].options.filter((_, idx) => idx !== optionIndex);
    setQuestions(updatedQuestions);
  };

  // --- LOGIC: SUBMISSION ---

  const handleSubmit = async () => {
    if (!title.trim()) {
      setError('Survey Title is required.');
      return;
    }

    setLoading(true);
    setError('');

    const payload = {
        title,
        description,
        questions: questions.map((q, index) => ({
            questionText: q.questionText,
            type: q.type,
    
            options: (q.type === 'MultipleChoice' || q.type === 'Checkbox') 
            ? JSON.stringify(q.options) 
            : null,
      
            order: index + 1
        }))
        };


    try {
      await surveyService.create(payload);
      navigate('/dashboard/survey-management');
    } catch (err) {
      console.error(err);
      setError(err.message || 'Failed to create survey.');
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Button 
        startIcon={<ArrowBackIcon />} 
        onClick={() => navigate('/dashboard/survey-management')}
        sx={{ mb: 2 }}
      >
        Back to List
      </Button>

      <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold', color: '#1a1a1a' }}>
        Create New Survey
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {/* SECTION 1: SURVEY DETAILS */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <TextField
          fullWidth
          label="Survey Title"
          variant="outlined"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          label="Description"
          multiline
          rows={2}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </Paper>

      {/* SECTION 2: QUESTIONS BUILDER */}
      <Typography variant="h5" sx={{ mb: 2, color: '#555' }}>Questions</Typography>
      
      {questions.map((q, qIndex) => (
        <Paper key={qIndex} sx={{ p: 3, mb: 3, position: 'relative', borderLeft: '6px solid #a8851d' }}>
          
          {/* Delete Button (Top Right) */}
          <IconButton 
            onClick={() => removeQuestion(qIndex)}
            sx={{ position: 'absolute', top: 10, right: 10, color: '#d32f2f' }}
            disabled={questions.length === 1}
          >
            <DeleteIcon />
          </IconButton>

          <Grid container spacing={2}>
            {/* Question Text */}
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                label={`Question ${qIndex + 1}`}
                value={q.questionText}
                onChange={(e) => updateQuestion(qIndex, 'questionText', e.target.value)}
                placeholder="e.g. What is the site condition?"
              />
            </Grid>
            
            {/* Question Type Selector */}
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={q.type}
                  label="Type"
                  onChange={(e) => updateQuestion(qIndex, 'type', e.target.value)}
                >
                  {QUESTION_TYPES.map((type) => (
                    <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Options Builder (Only for Multiple Choice / Checkbox) */}
          {(q.type === 'MultipleChoice' || q.type === 'Checkbox') && (
            <Box sx={{ mt: 2, pl: 2, borderLeft: '2px solid #eee' }}>
              <Typography variant="caption" sx={{ color: '#666', mb: 1, display: 'block' }}>
                Answer Options:
              </Typography>
              {q.options.map((option, oIndex) => (
                <Box key={oIndex} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <TextField
                    size="small"
                    value={option}
                    onChange={(e) => updateOption(qIndex, oIndex, e.target.value)}
                    sx={{ mr: 1, flexGrow: 1 }}
                  />
                  <IconButton size="small" onClick={() => removeOption(qIndex, oIndex)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
              <Button 
                size="small" 
                startIcon={<AddCircleIcon />} 
                onClick={() => addOption(qIndex)}
              >
                Add Option
              </Button>
            </Box>
          )}
        </Paper>
      ))}

      {/* SECTION 3: ACTIONS */}
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 4, mb: 8 }}>
        <Button 
          variant="outlined" 
          startIcon={<AddCircleIcon />} 
          onClick={addQuestion}
          size="large"
        >
          Add Question
        </Button>
        <Button 
          variant="contained" 
          startIcon={<SaveIcon />} 
          onClick={handleSubmit}
          disabled={loading}
          size="large"
          sx={{ bgcolor: '#a8851d', '&:hover': { bgcolor: '#8c6e18' } }}
        >
          {loading ? 'Publishing...' : 'Publish Survey'}
        </Button>
      </Box>

    </Container>
  );
};

export default CreateSurveyPage;